import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Link2,
  User,
  BarChart3,
  QrCode,
  Code,
  Layers,
  Download,
  Settings,
  HelpCircle,
  LogOut,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/ui';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const Sidebar = ({ open, onClose }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const mainNavItems: NavItem[] = [
    { label: t('nav.dashboard'), href: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { label: t('nav.deeplinks'), href: '/deeplinks', icon: <Link2 className="w-5 h-5" /> },
    { label: t('nav.profilePage'), href: '/profile', icon: <User className="w-5 h-5" /> },
    { label: t('nav.qrCodes'), href: '/qr-codes', icon: <QrCode className="w-5 h-5" /> },
    { label: t('nav.statistics'), href: '/stats', icon: <BarChart3 className="w-5 h-5" /> },
    { label: t('nav.linkPages'), href: '/linkpages', icon: <Layers className="w-5 h-5" /> },
    { label: t('nav.pixel'), href: '/pixel', icon: <Code className="w-5 h-5" /> },
    { label: t('nav.contents'), href: '/contents', icon: <Download className="w-5 h-5" /> },
  ];

  const secondaryNavItems: NavItem[] = [
    { label: t('nav.settings'), href: '/settings', icon: <Settings className="w-5 h-5" /> },
    { label: t('nav.help'), href: '/help', icon: <HelpCircle className="w-5 h-5" /> },
  ];

  const handleLogout = async () => {
    await logout();
    onClose();
    navigate('/login');
  };

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href;

    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200
          ${isActive
            ? 'bg-lime-400 text-dark-800 font-medium shadow-sm'
            : 'text-gray-600 hover:bg-gray-100 hover:text-dark-800 dark:text-gray-400 dark:hover:bg-dark-800 dark:hover:text-white'
          }
        `}
      >
        {item.icon}
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Overlay (mobile only) */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 md:top-16 bottom-0 w-64 bg-white border-r border-gray-100 overflow-y-auto z-50 md:z-30
          transition-transform duration-300 ease-in-out
          dark:bg-dark-900 dark:border-dark-700
          ${open ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        <div className="flex flex-col h-full p-4">
          {/* Mobile close button */}
          <div className="flex items-center justify-between mb-4 md:hidden">
            <Link to="/" className="flex items-center gap-2" onClick={onClose}>
              <Logo size="sm" />
            </Link>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-dark-800 dark:text-gray-400 dark:hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1">
            <div className="space-y-1">
              {mainNavItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>

            <hr className="my-6 border-gray-100 dark:border-dark-700" />

            <div className="space-y-1">
              {secondaryNavItems.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </div>

            <hr className="my-6 border-gray-100 dark:border-dark-700" />

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200 text-gray-600 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-900/30 w-full"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('nav.logOut')}</span>
            </button>
          </nav>

          {/* Legal Links */}
          <div className="mt-auto">
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-4 text-xs text-gray-400">
              <Link
                to="/terms"
                onClick={onClose}
                className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('footer.terms')}
              </Link>
              <span>·</span>
              <Link
                to="/privacy"
                onClick={onClose}
                className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('footer.privacy')}
              </Link>
              <span>·</span>
              <Link
                to="/cookies"
                onClick={onClose}
                className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('footer.cookies')}
              </Link>
              <span>·</span>
              <Link
                to="/legal"
                onClick={onClose}
                className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('footer.legalNotice')}
              </Link>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
